import { Link, Outlet } from 'react-router-dom';
import {
  useDisclosure,
  Flex,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useColorModeValue,
  Text,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { FiMenu, FiChevronDown, FiSettings } from 'react-icons/fi';
import { ElementType } from 'react';
import { useSignOut, useUserData } from '@nhost/react';
import { SidebarContent } from './SidebarContent';
import ErrorBoundary from './ErrorBoundry';
import BottomNavigation from './shared/BottomNavigation';

const Layout = () => {
  const sidebar = useDisclosure();
  const { signOut } = useSignOut();
  const userData = useUserData();

  // const {
  //   data: userData,
  //   loading,
  //   error,
  // } = useGetUserQuery({
  //   variables: { id: userId },
  // });

  // const {
  //   data: restaurantData,
  //   error: restaurantError,
  // } = useGetMyRestaurantQuery({
  //   variables: { id: userId },
  // });

  // const hasError = error || restaurantError;

  // if (hasError) {
  //   throw new Error(`Failed to fetch data: ${error?.message} ${restaurantError?.message}`);
  // }

  // const isLoading = loading || !userData;

  // const contextValue = useMemo(
  //   () => ({
  //     user: userData?.user,
  //     restaurant: restaurantData?.restaurant?.[0],
  //   }),
  //   [userData, restaurantData]
  // );

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mainBg = useColorModeValue('gray.50', 'gray.900');

  return (
    <Box as="section" minH="100vh" bg={mainBg}>
      {/* Desktop Sidebar */}
      <Box
        as="aside"
        transition="width .3s ease"
        w={{ base: 'full', md: sidebar.isOpen ? '60' : '20' }}
        display={{ base: sidebar.isOpen ? 'block' : 'none', md: 'block' }}
        pos="fixed"
        h="100vh"
        top={0}
        left={0}
        zIndex={20}
        boxShadow="lg"
        borderRight="1px"
        borderColor={borderColor}
      >
        <SidebarContent sidebar={sidebar as any} />
      </Box>

      {/* Mobile Overlay */}
      {sidebar.isOpen && (
        <Box
          display={{ base: 'block', md: 'none' }}
          pos="fixed"
          top={0}
          left={0}
          w="100vw"
          h="100vh"
          bg="blackAlpha.600"
          zIndex={10}
          onClick={sidebar.onClose}
          transition="all 0.3s"
          backdropFilter="blur(4px)"
        />
      )}

      <Box
        ml={{ base: 0, md: sidebar.isOpen ? '60' : '20' }}
        transition="all .3s ease"
        pb={{ base: '80px', md: 0 }} // Add bottom padding for mobile bottom nav
      >
        {/* Header */}
        <Flex
          as="header"
          align="center"
          justify="space-between"
          w="full"
          px={{ base: 4, md: 6 }}
          pos="sticky"
          top={0}
          bg={bgColor}
          borderBottomWidth="1px"
          borderColor={borderColor}
          h={{ base: '16', md: '14' }}
          zIndex={3}
          boxShadow="sm"
          backdropFilter="blur(10px)"
          sx={{
            '@supports (backdrop-filter: blur(10px))': {
              bg: useColorModeValue('rgba(255, 255, 255, 0.95)', 'rgba(26, 32, 44, 0.95)'),
            },
          }}
        >
          <HStack spacing={3}>
            <IconButton
              aria-label="Menu"
              onClick={sidebar.onToggle}
              icon={<Icon as={FiMenu as ElementType} />}
              variant="ghost"
              size="sm"
              display={{ base: 'flex', md: 'flex' }}
              _hover={{
                bg: useColorModeValue('gray.100', 'whiteAlpha.100'),
              }}
            />
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              fontWeight="bold"
              display={{ base: 'block', md: 'none' }}
              bgGradient="linear(to-r, brand.400, brand.600)"
              bgClip="text"
            >
              Magauu
            </Text>
          </HStack>

          <HStack spacing={4}>
            <Menu>
              <MenuButton py={2} transition="all 0.3s" _focus={{ boxShadow: 'none' }}>
                <HStack spacing={3}>
                  <Avatar
                    size={{ base: 'sm', md: 'sm' }}
                    name={userData?.displayName || ''}
                    bg="brand.500"
                    boxShadow="sm"
                  />
                  <Box display={{ base: 'none', md: 'block' }}>
                    <Text fontSize="sm" fontWeight="medium">
                      {userData?.displayName || 'User'}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {userData?.email}
                    </Text>
                  </Box>
                  <Box display={{ base: 'none', md: 'block' }}>
                    <Icon as={FiChevronDown as ElementType} />
                  </Box>
                </HStack>
              </MenuButton>
              <MenuList bg={bgColor} borderColor={borderColor} boxShadow="xl" borderRadius="xl" border="1px">
                <Link to="profile">
                  <MenuItem
                    icon={<Icon as={FiSettings as ElementType} />}
                    _hover={{
                      bg: useColorModeValue('gray.100', 'whiteAlpha.100'),
                    }}
                    borderRadius="md"
                  >
                    Profile Settings
                  </MenuItem>
                </Link>
                <MenuItem
                  onClick={signOut}
                  color="red.400"
                  _hover={{
                    bg: 'red.50',
                    _dark: {
                      bg: 'whiteAlpha.100',
                    },
                  }}
                  borderRadius="md"
                >
                  Logout
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </Flex>

        {/* Main Content */}
        <Box as="main" p={{ base: 4, md: 6 }} minH={{ base: 'calc(100vh - 64px - 80px)', md: 'calc(100vh - 56px)' }}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </Box>
      </Box>

      {/* Mobile Bottom Navigation */}
      <BottomNavigation />
    </Box>
  );
};

export default Layout;
